import React from 'react';
import Icon from '../../../components/AppIcon';

const TripSummaryPanel = ({ tripData }) => {
  return (
    <div className="bg-card border border-border rounded-organic p-6 shadow-organic-md">
      <h3 className="font-heading font-heading-bold text-lg text-card-foreground mb-4">Trip Summary</h3>
      <div className="space-y-4">
        {/* Duration */}
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary/10 rounded-organic-sm flex items-center justify-center">
            <Icon name="Calendar" size={16} color="var(--color-primary)" />
          </div>
          <div>
            <p className="font-body font-body-medium text-sm text-card-foreground">Duration</p>
            <p className="font-caption text-xs text-muted-foreground">{tripData?.duration}</p>
          </div>
        </div>

        {/* Featured Parks with Website Links */}
        <div className="flex items-start space-x-3">
          <div className="w-8 h-8 bg-success/10 rounded-organic-sm flex items-center justify-center mt-0.5">
            <Icon name="MapPin" size={16} color="var(--color-success)" />
          </div>
          <div className="flex-1">
            <p className="font-body font-body-medium text-sm text-card-foreground mb-1">Featured Parks</p>
            <div className="space-y-2">
              {tripData?.featuredParks?.map((park, index) => (
                <div key={index}>
                  <p className="font-caption text-xs text-card-foreground font-medium">{park}</p>
                  {/* Display official park website if available */}
                  {tripData?.reservationInfo?.officialParkWebsites?.find(site => 
                    site?.parkName?.toLowerCase()?.includes(park?.toLowerCase()?.split(' ')?.[0])
                  ) && (
                    <div className="mt-1">
                      {(() => {
                        const parkWebsite = tripData?.reservationInfo?.officialParkWebsites?.find(site => 
                          site?.parkName?.toLowerCase()?.includes(park?.toLowerCase()?.split(' ')?.[0])
                        );
                        return (
                          <div className="space-y-1">
                            <a 
                              href={parkWebsite?.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="font-caption text-xs text-primary hover:underline flex items-center space-x-1"
                            >
                              <Icon name="ExternalLink" size={10} />
                              <span>Official Website</span>
                            </a>
                            {parkWebsite?.reservationLink && (
                              <a 
                                href={parkWebsite?.reservationLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="font-caption text-xs text-success hover:underline flex items-center space-x-1"
                              >
                                <Icon name="Calendar" size={10} />
                                <span>Make Reservations</span>
                              </a>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* **ENHANCED**: Accommodation Summary with Website Links */}
        {tripData?.accommodations && (
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-orange/10 rounded-organic-sm flex items-center justify-center mt-0.5">
              <Icon name="Home" size={16} color="var(--color-orange)" />
            </div>
            <div className="flex-1">
              <p className="font-body font-body-medium text-sm text-card-foreground mb-2">Accommodations</p>
              {tripData?.accommodations?.campgrounds?.length > 0 && (
                <div className="mb-2">
                  <p className="font-caption text-xs font-medium text-card-foreground">Campgrounds:</p>
                  {tripData?.accommodations?.campgrounds?.slice(0, 2)?.map((campground, index) => (
                    <div key={index} className="ml-2 mb-2">
                      <p className="font-caption text-xs text-muted-foreground">
                        • {campground?.name} ({campground?.cost || 'Varies'})
                      </p>
                      {campground?.website && (
                        <a 
                          href={campground?.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="font-caption text-xs text-primary hover:underline flex items-center space-x-1 ml-2"
                        >
                          <Icon name="ExternalLink" size={10} />
                          <span>Campground Info</span>
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {tripData?.accommodations?.cabins?.length > 0 && (
                <div className="mb-2">
                  <p className="font-caption text-xs font-medium text-card-foreground">Cabins:</p>
                  {tripData?.accommodations?.cabins?.slice(0, 2)?.map((cabin, index) => (
                    <div key={index} className="ml-2 mb-2">
                      <p className="font-caption text-xs text-muted-foreground">
                        • {cabin?.name} ({cabin?.cost || 'Varies'})
                      </p>
                      {cabin?.website && (
                        <a 
                          href={cabin?.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="font-caption text-xs text-primary hover:underline flex items-center space-x-1 ml-2"
                        >
                          <Icon name="ExternalLink" size={10} />
                          <span>Cabin Details</span>
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {tripData?.accommodations?.nearbyLodging?.length > 0 && (
                <div>
                  <p className="font-caption text-xs font-medium text-card-foreground">Nearby Lodging:</p>
                  <p className="font-caption text-xs text-muted-foreground ml-2">
                    • {tripData?.accommodations?.nearbyLodging?.length} options available
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* **NEW**: Dining Options */}
        {tripData?.dining && (
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-green/10 rounded-organic-sm flex items-center justify-center mt-0.5">
              <Icon name="Utensils" size={16} color="var(--color-green)" />
            </div>
            <div className="flex-1">
              <p className="font-body font-body-medium text-sm text-card-foreground mb-2">Dining</p>
              {tripData?.dining?.onSite?.length > 0 && (
                <p className="font-caption text-xs text-muted-foreground mb-1">
                  • On-site options: {tripData?.dining?.onSite?.slice(0, 2)?.join(', ')}
                </p>
              )}
              {tripData?.dining?.nearby?.length > 0 && (
                <p className="font-caption text-xs text-muted-foreground mb-1">
                  • Nearby: {tripData?.dining?.nearby?.slice(0, 2)?.join(', ')}
                </p>
              )}
              {tripData?.dining?.campKitchens && (
                <p className="font-caption text-xs text-muted-foreground">
                  • Camp kitchens available
                </p>
              )}
            </div>
          </div>
        )}

        {/* Activity Types */}
        <div className="flex items-start space-x-3">
          <div className="w-8 h-8 bg-accent/10 rounded-organic-sm flex items-center justify-center mt-0.5">
            <Icon name="Activity" size={16} color="var(--color-accent)" />
          </div>
          <div className="flex-1">
            <p className="font-body font-body-medium text-sm text-card-foreground mb-2">Activities</p>
            <div className="flex flex-wrap gap-1">
              {tripData?.activityTypes?.map((activity, index) => (
                <span 
                  key={index}
                  className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-organic-sm font-caption"
                >
                  {activity}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Estimated Cost */}
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-warning/10 rounded-organic-sm flex items-center justify-center">
            <Icon name="DollarSign" size={16} color="var(--color-warning)" />
          </div>
          <div>
            <p className="font-body font-body-medium text-sm text-card-foreground">Estimated Cost</p>
            <p className="font-caption text-xs text-muted-foreground">{tripData?.estimatedCost}</p>
          </div>
        </div>

        {/* **ENHANCED**: Reservation Information with Official Websites */}
        {tripData?.reservationInfo && (
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-blue/10 rounded-organic-sm flex items-center justify-center mt-0.5">
              <Icon name="BookOpen" size={16} color="var(--color-blue)" />
            </div>
            <div className="flex-1">
              <p className="font-body font-body-medium text-sm text-card-foreground mb-1">Reservations</p>
              {tripData?.reservationInfo?.advanceBooking && (
                <p className="font-caption text-xs text-muted-foreground mb-1">
                  Book {tripData?.reservationInfo?.advanceBooking} in advance
                </p>
              )}
              {tripData?.reservationInfo?.website && (
                <div className="mb-2">
                  <a 
                    href={tripData?.reservationInfo?.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="font-caption text-xs text-primary hover:underline flex items-center space-x-1"
                  >
                    <Icon name="ExternalLink" size={10} />
                    <span>Main Reservation System</span>
                  </a>
                </div>
              )}
              {/* Display individual park websites */}
              {tripData?.reservationInfo?.officialParkWebsites?.length > 0 && (
                <div className="mt-2">
                  <p className="font-caption text-xs font-medium text-card-foreground mb-1">Park Websites:</p>
                  <div className="space-y-1">
                    {tripData?.reservationInfo?.officialParkWebsites?.map((parkSite, index) => (
                      <div key={index} className="ml-2">
                        <p className="font-caption text-xs text-muted-foreground">{parkSite?.parkName}:</p>
                        <div className="flex flex-col space-y-1 ml-2">
                          <a 
                            href={parkSite?.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="font-caption text-xs text-primary hover:underline flex items-center space-x-1"
                          >
                            <Icon name="ExternalLink" size={10} />
                            <span>Park Info</span>
                          </a>
                          {parkSite?.reservationLink && (
                            <a 
                              href={parkSite?.reservationLink} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="font-caption text-xs text-success hover:underline flex items-center space-x-1"
                            >
                              <Icon name="Calendar" size={10} />
                              <span>Reserve</span>
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Weather Info */}
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-secondary/10 rounded-organic-sm flex items-center justify-center">
            <Icon name="Cloud" size={16} color="var(--color-secondary)" />
          </div>
          <div>
            <p className="font-body font-body-medium text-sm text-card-foreground">Weather Forecast</p>
            <p className="font-caption text-xs text-muted-foreground">{tripData?.weatherForecast}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripSummaryPanel;